import { api } from './api'
import { Invoice, InvoiceCreate, Payment, PaginatedResponse, InsuranceClaim, InsuranceClaimCreate, InsuranceClaimAppealRequest, InsuranceClaimSearchParams } from '@/types'

export interface BillingSearchParams {
  patient_id?: string
  appointment_id?: string
  status?: string
  date_from?: string
  date_to?: string
  skip?: number
  limit?: number
}

export interface InvoiceCreateRequest {
  patient_id: string
  appointment_id?: string | null
  invoice_date: string
  due_date: string
  notes?: string
  line_items: {
    service_code: string
    service_description: string
    quantity: number
    unit_price: number
  }[]
  tax_rate?: number
  discount_type?: 'percentage' | 'fixed'
  discount_value?: number
  payment_method?: string | null
  status?: string
}

export interface PaymentCreateRequest {
  invoice_id: string
  amount: number
  payment_method: string
  paid_by: string
  notes?: string
}

export const billingApi = {
  getInvoices: async (params?: BillingSearchParams): Promise<PaginatedResponse<Invoice>> => {
    const searchParams = new URLSearchParams()

    if (params?.patient_id) searchParams.append('patient_id', params.patient_id)
    if (params?.appointment_id) searchParams.append('appointment_id', params.appointment_id)
    if (params?.status) searchParams.append('status', params.status)
    if (params?.date_from) searchParams.append('date_from', params.date_from)
    if (params?.date_to) searchParams.append('date_to', params.date_to)
    if (params?.skip !== undefined) searchParams.append('skip', params.skip.toString())
    if (params?.limit !== undefined) searchParams.append('limit', params.limit.toString())

    return api.get(`/billing/invoices?${searchParams.toString()}`)
  },

  getInvoice: async (id: string): Promise<Invoice> => {
    return api.get(`/billing/invoices/${id}`)
  },

  createInvoice: async (data: InvoiceCreate): Promise<Invoice> => {
    return api.post('/billing/invoices', data)
  },

  updateInvoice: async (id: string, data: Partial<InvoiceCreate>): Promise<Invoice> => {
    return api.put(`/billing/invoices/${id}`, data)
  },

  deleteInvoice: async (id: string): Promise<void> => {
    return api.delete(`/billing/invoices/${id}`)
  },

  processPayment: async (invoiceId: string, data: PaymentCreateRequest): Promise<Payment> => {
    return api.post(`/billing/invoices/${invoiceId}/payments`, data)
  },

  downloadInvoice: async (id: string, language: string = 'en'): Promise<Blob> => {
    return api.get(`/billing/invoices/${id}/download?language=${language}`, { responseType: 'blob' })
  },
}

// Insurance Claims
export const claimsApi = {
  getClaims: async (params?: InsuranceClaimSearchParams): Promise<PaginatedResponse<InsuranceClaim>> => {
    const searchParams = new URLSearchParams()

    if (params?.patient_id) searchParams.append('patient_id', params.patient_id)
    if (params?.status) searchParams.append('status', params.status)
    if (params?.claim_type) searchParams.append('claim_type', params.claim_type)
    if (params?.date_from) searchParams.append('date_from', params.date_from)
    if (params?.date_to) searchParams.append('date_to', params.date_to)
    if (params?.skip !== undefined) searchParams.append('skip', params.skip.toString())
    if (params?.limit !== undefined) searchParams.append('limit', params.limit.toString())

    return api.get(`/billing/claims?${searchParams.toString()}`)
  },

  getClaim: async (id: string): Promise<InsuranceClaim> => {
    return api.get(`/billing/claims/${id}`)
  },

  createClaim: async (data: InsuranceClaimCreate): Promise<InsuranceClaim> => {
    return api.post('/billing/claims', data)
  },

  submitClaim: async (id: string, submissionMethod: string = 'electronic'): Promise<InsuranceClaim> => {
    return api.post(`/billing/claims/${id}/submit?submission_method=${submissionMethod}`)
  },

  updateClaimStatus: async (id: string, status: string, notes?: string): Promise<InsuranceClaim> => {
    return api.patch(`/billing/claims/${id}/status`, { status, notes })
  },

  appealClaim: async (id: string, data: InsuranceClaimAppealRequest): Promise<InsuranceClaim> => {
    return api.post(`/billing/claims/${id}/appeal`, data)
  },

  deleteClaim: async (id: string): Promise<void> => {
    return api.delete(`/billing/claims/${id}`)
  },
}

